/**
 * Утилиты для страницы Live.
 *
 * Бэкенд на POST /group-match/event/{id}/change-user возвращает событие,
 * в котором может быть только часть команд (обычно команды одного хоста)
 * и порядок команд не гарантирован. Поэтому ответ не заменяет данные
 * целиком, а мержится в уже загруженное событие: команды других хостов
 * остаются на месте, порядок отображения не меняется.
 */

const asKey = (value) => String(value);

/** Единый порядок отображения команд: по номеру слота. */
export function sortTeamsBySlot(teams) {
    return [...(teams ?? [])].sort((a, b) => (a?.slot_number ?? 0) - (b?.slot_number ?? 0));
}

/** Мерж участников команды по user_id с сохранением исходного порядка. */
export function mergeMembers(currentMembers, incomingMembers) {
    const current = currentMembers ?? [];
    const incoming = incomingMembers ?? [];

    if (!incoming.length) return current;
    if (!current.length) return incoming;

    const incomingByUser = new Map(incoming.map((member) => [asKey(member?.user_id), member]));

    const merged = current.map((member) => {
        const key = asKey(member?.user_id);
        if (!incomingByUser.has(key)) return member;

        const fresh = incomingByUser.get(key);
        incomingByUser.delete(key);
        return {...member, ...fresh};
    });

    incomingByUser.forEach((member) => merged.push(member));

    return merged;
}

/** Мерж команд по team_id с сохранением исходного порядка отображения. */
export function mergeTeams(currentTeams, incomingTeams) {
    const current = currentTeams ?? [];
    const incoming = incomingTeams ?? [];

    if (!current.length) return sortTeamsBySlot(incoming);
    if (!incoming.length) return current;

    const incomingByTeam = new Map(incoming.map((team) => [asKey(team?.team_id), team]));

    const merged = current.map((team) => {
        const key = asKey(team?.team_id);
        if (!incomingByTeam.has(key)) return team;

        const fresh = incomingByTeam.get(key);
        incomingByTeam.delete(key);
        return {...team, ...fresh, members: mergeMembers(team.members, fresh.members)};
    });

    // команды, которых не было в загруженном событии, добавляем в конец
    incomingByTeam.forEach((team) => merged.push(team));

    return merged;
}

/**
 * Обновляет загруженное событие данными из ответа записывающего запроса.
 * @param {Object|null} currentEvent событие, которое уже показано на странице
 * @param {Object|null} incomingEvent событие из ответа бэкенда
 */
export function mergeEventData(currentEvent, incomingEvent) {
    if (!incomingEvent) return currentEvent;
    if (!currentEvent) return {...incomingEvent, teams: sortTeamsBySlot(incomingEvent.teams)};

    return {
        ...currentEvent,
        ...incomingEvent,
        teams: mergeTeams(currentEvent.teams, incomingEvent.teams),
    };
}

/** Команды одного хоста (у команд хоста одинаковый hash-токен). */
export function teamsOfHost(teams, hash) {
    return (teams ?? []).filter((team) => asKey(team?.hash) === asKey(hash));
}

/** Список хостов матча: по одной команде на каждый уникальный hash, в порядке слотов. */
export function uniqueHostsFromTeams(teams) {
    const seen = new Set();

    return sortTeamsBySlot(teams).filter((team) => {
        const key = asKey(team?.hash);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** Игрок выбыл: бэкенд присылает death > 0. */
export function isMemberDead(member) {
    return Number(member?.death ?? 0) > 0;
}

/** Команда выбыла полностью: все её игроки мертвы. */
export function isTeamEliminated(team) {
    const members = team?.members ?? [];
    return members.length > 0 && members.every(isMemberDead);
}

/** Место проставлено (0/null/undefined — места ещё нет). */
export function hasPlace(team) {
    return Number(team?.place ?? 0) > 0;
}

/**
 * Порядок отображения общего списка: сначала команды без места (ещё в игре),
 * затем выбывшие по возрастанию места — последний выбывший выше первого.
 * При равенстве — по номеру слота, чтобы порядок не «прыгал».
 */
export function sortTeamsByPlace(teams) {
    return [...(teams ?? [])].sort((a, b) => {
        const placeA = Number(a?.place ?? 0) || 0;
        const placeB = Number(b?.place ?? 0) || 0;

        if (placeA !== placeB) {
            if (!placeA) return -1;
            if (!placeB) return 1;
            return placeA - placeB;
        }

        return (a?.slot_number ?? 0) - (b?.slot_number ?? 0);
    });
}

/**
 * Автоматическая простановка места выбывшим командам.
 *
 * Первая полностью выбывшая команда получает последнее место (= число команд),
 * вторая — предпоследнее и так далее. Порядок выбывания хранится отдельно
 * (previousOrder), потому что ответ бэкенда на change-user приходит с place: 0
 * и при мерже затёр бы уже проставленные места.
 *
 * Чужие места (проставленные вручную или пришедшие с бэкенда у завершённого
 * матча) не трогаем и считаем занятыми.
 *
 * @param {Object|null} event событие со списком команд
 * @param {Array} previousOrder id команд в порядке выбывания с прошлого пересчёта
 * @returns {{event: Object|null, order: Array<string>}}
 */
export function applyEliminationPlaces(event, previousOrder = []) {
    const teams = event?.teams ?? [];
    if (!teams.length) return {event, order: []};

    const eliminated = new Set(teams.filter(isTeamEliminated).map((team) => asKey(team?.team_id)));
    const known = (previousOrder ?? []).map(asKey).filter((id) => eliminated.has(id));
    const knownSet = new Set(known);

    const teamsByKey = new Map(teams.map((team) => [asKey(team?.team_id), team]));

    // команды, выбывшие между пересчётами: место назначаем в порядке слотов
    const appeared = sortTeamsBySlot(teams)
        .map((team) => asKey(team?.team_id))
        .filter((id) => eliminated.has(id) && !knownSet.has(id) && !hasPlace(teamsByKey.get(id)));

    const order = [...known, ...appeared];
    const orderSet = new Set(order);

    const occupied = new Set(
        teams
            .filter((team) => hasPlace(team) && !orderSet.has(asKey(team?.team_id)))
            .map((team) => Number(team.place))
    );

    let cursor = teams.length;
    const takeNextPlace = () => {
        while (cursor > 0 && occupied.has(cursor)) cursor -= 1;
        if (cursor < 1) return 0;

        const place = cursor;
        occupied.add(place);
        cursor -= 1;
        return place;
    };

    const placeByTeam = new Map(order.map((id) => [id, takeNextPlace()]));
    const revived = new Set((previousOrder ?? []).map(asKey).filter((id) => !eliminated.has(id)));

    const nextTeams = teams.map((team) => {
        const id = asKey(team?.team_id);

        if (placeByTeam.has(id)) {
            const place = placeByTeam.get(id);
            return Number(team?.place ?? 0) === place ? team : {...team, place};
        }

        // команду «оживили» — автоматически проставленное место снимаем
        if (revived.has(id)) {
            return Number(team?.place ?? 0) === 0 ? team : {...team, place: 0};
        }

        return team;
    });

    return {event: {...event, teams: nextTeams}, order};
}
